import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import ChatHeader from './ChatHeader';
import ChatArea from './ChatArea';
import ChatInputBar from './ChatInputBar';
import { useToast } from '@/components/organisms/feedback/use-toast';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import useOnboardingChatHistory from '@/services/admin-services/query/useOnboardingChatHistory';
import useOnboardingSendChatMessage from '@/services/admin-services/mutation/useOnboardingSendChatMessage';
import useOnboardingEditChatMessage from '@/services/admin-services/mutation/useOnboardingEditChatMessage';
import useOnboardingDeleteChatMessage from '@/services/admin-services/mutation/useOnboardingDeleteChatMessage';
import useOnboardingMarkMessagesAsRead from '@/services/admin-services/mutation/useOnboardingMarkMessagesAsRead';
import useOnboardingCandidateEvents from '@/services/admin-services/query/useOnboardingCandidateEvents';
import useOnboardingChatMessageEvent, { CHAT_MESSAGE_EVENT_TYPES } from '@/services/admin-services/subscription/useOnboardingChatMessageEvent';
import { useTranslation } from 'react-i18next';

// Le chat est temps reel via subscription multipart : seuls les evenements de profil
// sont encore pulles, faute de subscription `onboardingCandidateEvent` cote backend.
const EVENTS_POLLING_WHEN_OPEN_MS = 60000;
const EVENTS_POLLING_WHEN_CLOSED_MS = 120000;
const MOBILE_BREAKPOINT_PX = 640;
const CHAT_DISPLAY_MODE_STORAGE_KEY = 'onboarding-chat-display-mode';

const toPreview = (content) => {
    const normalized = String(content || '').trim();
    if (!normalized) return '';
    return normalized.length > 72 ? `${normalized.slice(0, 72)}...` : normalized;
};

const mergeUniqueMessages = (baseMessages, incomingMessages) => {
    const byUuid = new Map();
    [...baseMessages, ...incomingMessages].forEach((message) => {
        const existingMessage = byUuid.get(message.uuid);
        // Fusion et non remplacement : les evenements de subscription ne portent pas
        // `editable`/`deletedAt`, qui doivent survivre a une mise a jour partielle.
        byUuid.set(message.uuid, existingMessage ? { ...existingMessage, ...message } : message);
    });

    return [...byUuid.values()].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
};

const mergeUniqueEvents = (baseEvents, incomingEvents) => {
    const byKey = new Map();

    [...baseEvents, ...incomingEvents].forEach((event) => {
        const key = `${event.timestamp}-${event.eventCode}-${event.eventMessage || ''}-${event.additionalInfo || ''}`;
        byKey.set(key, event);
    });

    return [...byKey.values()].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

const ChatSection = ({ candidateUuid, candidateName, onAnchorStateChange }) => {
    const [t] = useTranslation('global');
    const { toast } = useToast();
    const [sliceNumber, setSliceNumber] = useState(1);
    const [messages, setMessages] = useState([]);
    const [profileEvents, setProfileEvents] = useState([]);
    const [composerValue, setComposerValue] = useState('');
    const [editingMessageUuid, setEditingMessageUuid] = useState(null);
    const [scrollToBottomSignal, setScrollToBottomSignal] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [isAnchored, setIsAnchored] = useState(() => window.localStorage.getItem(CHAT_DISPLAY_MODE_STORAGE_KEY) === 'anchored');
    const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
    const isMarkingReadRef = useRef(false);
    const hasHydratedEventsRef = useRef(false);

    const { data: chatData, loading: chatLoading, error: chatError, refetch: refetchChat } = useOnboardingChatHistory(candidateUuid, sliceNumber);
    const {
        data: candidateEvents,
        loading: candidateEventsLoading,
        refetch: refetchCandidateEvents,
    } = useOnboardingCandidateEvents(candidateUuid);
    const { sendChatMessage, loading: sendMessageLoading } = useOnboardingSendChatMessage();
    const { editChatMessage, loading: editMessageLoading } = useOnboardingEditChatMessage();
    const { deleteChatMessage, loading: deleteMessageLoading } = useOnboardingDeleteChatMessage();
    const { markMessagesAsRead } = useOnboardingMarkMessagesAsRead();

    useEffect(() => {
        setSliceNumber(1);
        setMessages([]);
        setProfileEvents([]);
        setComposerValue('');
        setEditingMessageUuid(null);
        setIsOpen(false);
        hasHydratedEventsRef.current = false;
    }, [candidateUuid]);

    useEffect(() => {
        if (!chatData?.messages) return;
        setMessages((previous) => mergeUniqueMessages(previous, chatData.messages));
    }, [chatData]);

    useEffect(() => {
        if (!candidateEvents) return;

        setProfileEvents((previous) => {
            const mergedEvents = mergeUniqueEvents(previous, candidateEvents);

            if (hasHydratedEventsRef.current && mergedEvents.length > previous.length) {
                setScrollToBottomSignal((current) => current + 1);
            }

            hasHydratedEventsRef.current = true;
            return mergedEvents;
        });
    }, [candidateEvents]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        window.localStorage.setItem(CHAT_DISPLAY_MODE_STORAGE_KEY, isAnchored ? 'anchored' : 'floating');
    }, [isAnchored]);

    useEffect(() => {
        onAnchorStateChange?.(isAnchored);
    }, [isAnchored, onAnchorStateChange]);

    useEffect(() => {
        if (!candidateUuid) return undefined;

        const pollingInterval = isOpen || isAnchored
            ? EVENTS_POLLING_WHEN_OPEN_MS
            : EVENTS_POLLING_WHEN_CLOSED_MS;

        const intervalId = setInterval(() => {
            void refetchCandidateEvents();
        }, pollingInterval);

        return () => {
            clearInterval(intervalId);
        };
    }, [candidateUuid, isOpen, isAnchored, refetchCandidateEvents]);

    // Retour sur l'onglet : on rattrape immediatement sans attendre la fin de l'intervalle.
    useEffect(() => {
        if (!candidateUuid) return undefined;

        const handleFocus = () => {
            void refetchCandidateEvents();
        };

        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [candidateUuid, refetchCandidateEvents]);

    const handleChatMessageEvent = useCallback((event) => {
        const incomingMessage = event?.message;
        // Le flux est global cote admin : on ne garde que la conversation affichee.
        if (!incomingMessage || incomingMessage.candidateUuid !== candidateUuid) return;

        if (event.eventType === CHAT_MESSAGE_EVENT_TYPES.DELETE) {
            setMessages((previous) => previous.map((current) => (
                current.uuid === incomingMessage.uuid
                    ? { ...current, deletedAt: new Date().toISOString(), content: '' }
                    : current
            )));
            return;
        }

        if (event.eventType === CHAT_MESSAGE_EVENT_TYPES.NEW && !incomingMessage.adminSender) {
            setScrollToBottomSignal((previous) => previous + 1);
        }

        setMessages((previous) => mergeUniqueMessages(previous, [incomingMessage]));
    }, [candidateUuid]);

    const handleChatStreamResync = useCallback(() => {
        void refetchChat();
    }, [refetchChat]);

    useOnboardingChatMessageEvent({
        onEvent: handleChatMessageEvent,
        onResync: handleChatStreamResync,
        skip: !candidateUuid,
    });

    const unreadCount = useMemo(() => {
        return messages.filter((message) => !message.adminSender && !message.adminReadAt && !message.deletedAt).length;
    }, [messages]);

    const currentEditedMessage = useMemo(() => {
        return messages.find((message) => message.uuid === editingMessageUuid) || null;
    }, [messages, editingMessageUuid]);

    const latestUnread = useMemo(() => {
        const unreadInboundMessages = messages.filter((message) => {
            return !message.adminSender && !message.adminReadAt && !message.deletedAt;
        });
        return unreadInboundMessages[unreadInboundMessages.length - 1] || null;
    }, [messages]);

    useEffect(() => {
        if (editingMessageUuid && currentEditedMessage) {
            setComposerValue(currentEditedMessage.content || '');
        }
    }, [editingMessageUuid, currentEditedMessage]);

    const markAllMessagesAsRead = async () => {
        if (!candidateUuid || unreadCount === 0 || isMarkingReadRef.current) return;

        isMarkingReadRef.current = true;
        const nowIso = new Date().toISOString();

        setMessages((previous) => previous.map((message) => (
            message.adminSender || message.adminReadAt || message.deletedAt
                ? message
                : { ...message, adminReadAt: nowIso }
        )));

        try {
            await markMessagesAsRead(candidateUuid);
        } catch {
            // silent: auto-marking should not interrupt user flow
        } finally {
            isMarkingReadRef.current = false;
        }
    };

    const handleSendOrSave = async () => {
        const content = composerValue.trim();
        if (!candidateUuid || !content) return;

        if (editingMessageUuid) {
            try {
                const response = await editChatMessage(editingMessageUuid, content);
                const updatedMessage = response?.data?.onboardingEditChatMessage;
                if (updatedMessage) {
                    setMessages((previous) => previous.map((message) => (
                        message.uuid === editingMessageUuid
                            ? { ...message, ...updatedMessage, modified: true }
                            : message
                    )));
                }
                setEditingMessageUuid(null);
                setComposerValue('');
            } catch (error) {
                toast({
                    title: t('management.onboarding.feedback.editMessageErrorTitle'),
                    description: error?.message || t('error.fetchUnsuccessfull'),
                    variant: 'error',
                });
            }
            return;
        }

        const tempUuid = `temp-${Date.now()}`;
        const optimisticMessage = {
            uuid: tempUuid,
            candidateUuid,
            senderName: t('management.onboarding.chat.adminSender'),
            adminSender: true,
            content,
            modified: false,
            editable: true,
            createdAt: new Date().toISOString(),
            adminReadAt: new Date().toISOString(),
            candidateReadAt: null,
        };

        setScrollToBottomSignal((previous) => previous + 1);
        setMessages((previous) => mergeUniqueMessages(previous, [optimisticMessage]));
        setComposerValue('');

        try {
            const response = await sendChatMessage(candidateUuid, content);
            const savedMessage = response?.data?.onboardingSendChatMessage;

            if (savedMessage) {
                // La subscription peut avoir devance la reponse de la mutation : on retire
                // le message optimiste puis on fusionne pour eviter tout doublon d'uuid.
                setMessages((previous) => mergeUniqueMessages(
                    previous.filter((message) => message.uuid !== tempUuid),
                    [savedMessage]
                ));
            }
        } catch (error) {
            setMessages((previous) => previous.filter((message) => message.uuid !== tempUuid));
            toast({
                title: t('management.onboarding.feedback.sendMessageErrorTitle'),
                description: error?.message || t('error.fetchUnsuccessfull'),
                variant: 'error',
            });
        }
    };

    const handleDeleteMessage = async (message) => {
        if (!message?.uuid) return;
        try {
            await deleteChatMessage(message.uuid);
            setMessages((previous) => previous.map((current) => (
                current.uuid === message.uuid
                    ? { ...current, deletedAt: new Date().toISOString(), content: '' }
                    : current
            )));
        } catch (error) {
            toast({
                title: t('management.onboarding.feedback.deleteMessageErrorTitle'),
                description: error?.message || t('error.fetchUnsuccessfull'),
                variant: 'error',
            });
        }
    };

    const handleEditMessage = (message) => {
        if (!message?.editable || message?.deletedAt) return;
        setEditingMessageUuid(message.uuid);
    };

    const handleUnreadMessageClick = (message) => {
        if (!message || message.adminSender || message.adminReadAt || message.deletedAt || (!isOpen && !isAnchored)) {
            return;
        }

        const nowIso = new Date().toISOString();
        setMessages((previous) => previous.map((current) => (
            current.uuid === message.uuid ? { ...current, adminReadAt: nowIso } : current
        )));
    };

    const handleLoadOlder = () => {
        if (chatLoading || !chatData?.hasNext) return;
        setSliceNumber((previous) => previous + 1);
    };

    const handleCancelEdit = () => {
        setEditingMessageUuid(null);
        setComposerValue('');
    };

    const handleOpenFloatingChat = () => {
        setIsOpen(true);
        if (!isAnchored) {
            void markAllMessagesAsRead();
        }
    };

    const handleComposerChange = (nextValue) => {
        const startsTyping = !composerValue.trim() && String(nextValue || '').trim().length > 0;
        if ((isOpen || isAnchored) && startsTyping) {
            void markAllMessagesAsRead();
        }
        setComposerValue(nextValue);
    };

    const inputLoading = sendMessageLoading || editMessageLoading || deleteMessageLoading;
    const isMobile = windowWidth < MOBILE_BREAKPOINT_PX;
    const showNotifPill = !isOpen && !isAnchored && Boolean(latestUnread);
    const unreadPreview = toPreview(latestUnread?.content);

    const handleToggleAnchor = () => {
        setIsAnchored((previous) => {
            const next = !previous;
            if (next) {
                setIsOpen(false);
            }
            return next;
        });
    };

    const chatWindow = (
        <>
            <ChatHeader
                unreadCount={unreadCount}
                candidateName={candidateName}
                onClose={isAnchored ? null : () => setIsOpen(false)}
                isAnchored={isAnchored}
                onToggleAnchor={handleToggleAnchor}
            />

            <ChatArea
                conversationKey={candidateUuid}
                scrollToBottomSignal={scrollToBottomSignal}
                messages={messages}
                profileEvents={profileEvents}
                loading={chatLoading}
                eventsLoading={candidateEventsLoading}
                error={chatError}
                hasNextPage={Boolean(chatData?.hasNext)}
                onLoadOlder={handleLoadOlder}
                onEdit={handleEditMessage}
                onDelete={handleDeleteMessage}
                editingMessageUuid={editingMessageUuid}
                onUnreadMessageClick={handleUnreadMessageClick}
            />

            {!chatLoading && chatError && (
                <div className="px-secondDegree pb-firstDegree">
                    <Button
                        style="ghost"
                        sizePadding="none"
                        text="management.onboarding.actions.refresh"
                        className="rounded-sm text-sm"
                        onClick={() => {
                            void refetchChat();
                            void refetchCandidateEvents();
                        }}
                    />
                </div>
            )}

            <ChatInputBar
                value={composerValue}
                onChange={handleComposerChange}
                onSend={handleSendOrSave}
                sending={inputLoading}
                isEditing={Boolean(editingMessageUuid)}
                onCancelEdit={handleCancelEdit}
            />
        </>
    );

    return (
        <>
            {isAnchored && (
                <section
                    className="min-h-[400px]  h-[min(66vh,800px)]  flex flex-col rounded-sm overflow-hidden my-firstDegree  bg-white  xl:sticky xl:top-firstDegree "
                    aria-label={`${t('management.onboarding.chat.title')} - ${candidateName}`}
                >
                    {chatWindow}
                </section>
            )}

            {!isAnchored && isOpen && isMobile && (
                <div
                    className="fixed inset-0 z-50 flex flex-col bg-white sm:hidden"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('management.onboarding.chat.title')}
                >
                    {chatWindow}
                </div>
            )}

            {!isAnchored && isOpen && !isMobile && (
                <section
                    className="fixed z-50 hidden h-[560px] w-[380px] flex-col overflow-hidden rounded-[20px] border border-primary/10 bg-white shadow-[0_8px_32px_rgba(27,52,119,0.18)] sm:flex bottom-5 right-4 sm:bottom-3 sm:right-6"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${t('management.onboarding.chat.title')} - ${candidateName}`}
                >
                    {chatWindow}
                </section>
            )}

            {!isOpen && !isAnchored && (
                <div className="fixed z-50 flex flex-col items-end gap-2.5 bottom-5 right-4 sm:bottom-3 sm:right-6">
                    {showNotifPill && (
                        <button
                            type="button"
                            className="max-w-[300px] rounded-[16px] rounded-bl-[6px] border border-primary/20 bg-white px-3 py-2 text-left shadow-[0_4px_20px_rgba(27,52,119,0.14)]"
                            onClick={handleOpenFloatingChat}
                            aria-label={t('management.onboarding.chat.title')}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <Label size="small" fontWeight="bold" message="management.onboarding.chat.title" />
                                {/* <Label size="small" className="rounded-full bg-information20 px-2 py-0.5 text-information-foreground">
                                    {t('management.onboarding.chat.unreadCount', { count: unreadCount })}
                                </Label> */}
                            </div>
                            <Label className="mt-1" color="grey">{unreadPreview}</Label>
                        </button>
                    )}

                    <Button
                        style="fill"
                        text="management.onboarding.chat.title"
                        showText={false}
                        showLeftIcon
                        leftIcon={<MessageSquare className="h-5 w-5" />}
                        className="h-14 w-14 rounded-full shadow-[0_8px_24px_rgba(24,95,165,0.34)] relative"
                        onClick={handleOpenFloatingChat}
                    />

                    {unreadCount > 0 && (
                        <div className="absolute aspect-square -right-1 -top-1 inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-error px-1.5 ">
                            <Label color="white" fontWeight="bold">{unreadCount > 99 ? '99+' : unreadCount}</Label>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default ChatSection;
